import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../components/auth/AuthContext";
import { useTranslation } from "react-i18next";
import { useFeatureFlags, useIsAdmin } from "../hooks/useFeatureFlags";
import { friendlyMessageForApiError } from "../api/errorUtils";
import ErrorDetailsAccordion from "../components/ui/ErrorDetailsAccordion";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getDatabases,
  getDatabaseIndexes,
  addDatabaseIndexes,
  deleteDatabaseIndex,
  updateDatabaseIndex,
  createColumnIndex,
  dropColumnIndex,
  deleteDatabase,
  purgeDatabase,
  setDatabaseClosed,
  activateDatabase,
  getProvisioningScript,
  getDatabaseStats,
  exportDatabaseConfig,
  importDatabaseConfig,
  exportDatabasesBundle,
  importDatabasesBundle,
} from "../api/adminApi";
import DatabaseStackRail from "../components/indexes/DatabaseStackRail";
import DatabaseForegroundPanel from "../components/indexes/DatabaseForegroundPanel";
import DatabaseHeader from "../components/indexes/DatabaseHeader";
import DatabaseKpiStrip from "../components/indexes/DatabaseKpiStrip";
import DatabasePropertiesPanel from "../components/indexes/DatabasePropertiesPanel";
import IndexesTable from "../components/indexes/IndexesTable";
import DeleteDatabaseModal from "../components/indexes/DeleteDatabaseModal";
import DbIndexConfirmModal from "../components/indexes/DbIndexConfirmModal";
import ConfirmModal from "../components/common/ConfirmModal";
import FastSearchImportModal from "../components/indexes/FastSearchImportModal";
import BulkDeleteDatabasesModal from "../components/indexes/BulkDeleteDatabasesModal";
import CreateDatabaseModal from "../components/indexes/CreateDatabaseModal";
import BrowseRecordsPanel from "../components/indexes/BrowseRecordsPanel";
import BundlePickerModal from "../components/indexes/BundlePickerModal";
import StorageLocationsModal from "../components/indexes/StorageLocationsModal";
import { Hero } from "../components/adminSurface/AdminSurface";
import { StackArt } from "../components/adminSurface/heroArt";

function Alert({ type = "info", children, onClose }) {
  const bg =
    type === "error" ? "#ffe9e9" : type === "success" ? "#e9f7ef" : "#f6f8ff";
  const color =
    type === "error" ? "#8b0000" : type === "success" ? "#116622" : "#264b9a";
  return (
    <div
      style={{
        background: bg,
        color,
        padding: 10,
        borderRadius: 8,
        border: `1px solid ${type === "error" ? "#f3c6c6" : "#cfe8d6"}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 700 }}>{children}</div>
      {onClose && (
        <button className="link-btn" onClick={onClose}>
          Close
        </button>
      )}
    </div>
  );
}

export default function AdminIndexes() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const features = useFeatureFlags();
  const isAdmin = useIsAdmin();
  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState("");
  const [indexes, setIndexes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSavingIndex, setIsSavingIndex] = useState(false);
  const [error, setError] = useState("");
  const [rawErrorDetails, setRawErrorDetails] = useState(null);
  const [, setShowErrorDetails] = useState(false);
  const [success, setSuccess] = useState("");

  const [dbStats, setDbStats] = useState(null);
  const [dbSearch, setDbSearch] = useState("");
  const [deleteModalTarget, setDeleteModalTarget] = useState(null);
  const [dbIndexConfirm, setDbIndexConfirm] = useState(null);
  const [deleteFieldTarget, setDeleteFieldTarget] = useState(null);
  const [lenConfirmTarget, setLenConfirmTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("indexes");
  const addFormRef = useRef(null);

  const [newFieldName, setNewFieldName] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newDataType, setNewDataType] = useState("string");
  const [newIsDeletable, setNewIsDeletable] = useState(true);
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStorageLocations, setShowStorageLocations] = useState(false);
  const importFileRef = useRef(null);
  const importBundleFileRef = useRef(null);
  // Fast Search rebuild is asked per-import via a modal (not a persistent toggle).
  // pendingImport: { kind: "single" | "bundle", config?, bundle? } | null
  const [pendingImport, setPendingImport] = useState(null);
  const [showBundlePicker, setShowBundlePicker] = useState(false);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [bundleError, setBundleError] = useState("");

  // ── Bulk selection / delete ────────────────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedDbIds, setSelectedDbIds] = useState([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const currentUserDisplayName =
    (user && (user.server?.name || user.name || user.email)) || "";

  function getDbId(db) {
    return db.id ?? db.Id ?? db.name ?? db.Name ?? db.dbName ?? db.DbName ?? "";
  }

  function getDbLabel(db) {
    return (
      db.displayName ||
      db.DisplayName ||
      db.name ||
      db.Name ||
      db.dbDisplayName ||
      db.DbDisplayName ||
      db.dbName ||
      db.DbName ||
      ""
    );
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const dbs = await getDatabases(token);
        setDatabases(dbs || []);
        if (dbs && dbs.length > 0) {
          const first = dbs[0];
          setSelectedDb(getDbId(first));
        }
      } catch (e) {
        setError(friendlyMessageForApiError(e));
        setRawErrorDetails(e && e.details ? e.details : e);
        setShowErrorDetails(false);
        console.warn("AdminIndexes: API error details:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  useEffect(() => {
    if (!selectedDb) return;
    setActiveTab("indexes");
    setShowInlineAdd(false);
    setDbStats(null);
    async function loadIdx() {
      setLoading(true);
      try {
        const [idxRes, statsRes] = await Promise.allSettled([
          getDatabaseIndexes(token, selectedDb),
          getDatabaseStats(token, selectedDb),
        ]);
        if (idxRes.status === "fulfilled") setIndexes(idxRes.value?.indexes || idxRes.value || []);
        else setError(idxRes.reason?.message || String(idxRes.reason));
        if (statsRes.status === "fulfilled") setDbStats(statsRes.value);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    }
    loadIdx();
  }, [token, selectedDb]);

  useEffect(() => {
    if (success) {
      const tId = setTimeout(() => setSuccess(""), 4000);
      return () => clearTimeout(tId);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const tId = setTimeout(() => setError(""), 6000);
      return () => clearTimeout(tId);
    }
  }, [error]);

  function validateFieldName(name) {
    if (!name || !name.trim()) return t("admin.fieldRequired");
    if (!/^[A-Za-z0-9_]+$/.test(name)) return t("admin.fieldInvalid");
    if (
      indexes.some(
        (i) =>
          String(i.fieldName || i.FieldName || i.Field).toLowerCase() ===
          name.toLowerCase()
      )
    )
      return t("admin.fieldExists");
    return "";
  }

  async function handleAdd(e) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setError("");
    setSuccess("");
    if (!selectedDb) {
      setError(t("admin.selectDatabasePrompt"));
      return;
    }
    const v = validateFieldName(newFieldName);
    if (v) {
      setError(v);
      return;
    }
    const item = {
      fieldName: newFieldName,
      fieldDisplayName: newDisplayName || newFieldName,
      dataType: newDataType,
      isDeletable: newIsDeletable,
    };
    try {
      setLoading(true);
      setIsSavingIndex(true);
      await addDatabaseIndexes(token, selectedDb, [item]);
      const res = await getDatabaseIndexes(token, selectedDb);
      setIndexes(res?.indexes || res || []);
      setNewFieldName("");
      setNewDisplayName("");
      setNewDataType("string");
      setNewIsDeletable(true);
      setSuccess(t("admin.indexAdded"));
    } catch (e) {
      setError(friendlyMessageForApiError(e));
      setRawErrorDetails(e && e.details ? e.details : e);
      setShowErrorDetails(false);
      console.warn("AdminIndexes: API error details:", e);
    } finally {
      setLoading(false);
      setIsSavingIndex(false);
    }
  }

  function handleDelete(fieldName, createdBy, isDeletableFlag) {
    if (!selectedDb) return;
    // double-check client-side: block if not deletable and user is not admin
    if (!isDeletableFlag && !isAdmin) {
      setError(t("admin.notDeletable"));
      return;
    }
    setDeleteFieldTarget({ fieldName });
  }

  async function performDeleteField(fieldName) {
    if (!selectedDb) return;
    try {
      await deleteDatabaseIndex(token, selectedDb, fieldName);
      const res = await getDatabaseIndexes(token, selectedDb);
      setIndexes(res?.indexes || res || []);
      setSuccess(t("admin.indexDeleted"));
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  async function handleUpdateDataType(fieldName, dataType, inputFormat) {
    if (!selectedDb) return;
    try {
      await updateDatabaseIndex(token, selectedDb, fieldName, dataType, inputFormat);
      setIndexes(prev => prev.map(ix =>
        ix.fieldName === fieldName ? { ...ix, dataType, formatting: inputFormat ?? ix.formatting } : ix
      ));
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  // A length edit is the one change on this tab that alters the live column, so
  // (unlike the instant datatype edit) ask for confirmation first. The row commits
  // the new value on Enter/blur; we hold it here and only apply on confirm.
  function handleUpdateLength(fieldName, newLength) {
    if (!selectedDb) return;
    const ix = indexes.find(i => i.fieldName === fieldName);
    if (!ix) return;
    setLenConfirmTarget({ fieldName, newLength, currentLength: ix.length ?? null });
  }

  // Widen a text column's length (increase-only). In locked-down Oracle the server
  // returns a DBA script instead of altering — download it and leave the displayed
  // length unchanged until the DBA runs it (re-submitting the same value then syncs it).
  async function performUpdateLength(fieldName, newLength) {
    if (!selectedDb) return;
    const ix = indexes.find(i => i.fieldName === fieldName);
    if (!ix) return;
    try {
      const res = await updateDatabaseIndex(token, selectedDb, fieldName, ix.dataType, ix.formatting, newLength);
      if (res && res.success === false) {
        setError(res.message || t("admin.widenFailed", { defaultValue: "Could not widen the column." }));
        return;
      }
      if (res?.pending && res?.script) {
        downloadSqlText(res.script, `widen_${fieldName}`);
        setSuccess(res.message || t("admin.widenPendingDba", {
          defaultValue: "Widening this column needs a schema change. A script was downloaded — have your DBA run it as the owner schema, then re-apply to confirm.",
        }));
        return;
      }
      setIndexes(prev => prev.map(i =>
        i.fieldName === fieldName ? { ...i, length: newLength } : i
      ));
      setSuccess(t("admin.widenSuccess", {
        defaultValue: "Column '{{field}}' widened to {{n}} characters.",
        field: fieldName, n: newLength,
      }));
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  // Toggle a real (physical) database index on a column. Opens the in-app confirm
  // modal; the actual create/drop runs in performDbIndexToggle on confirm.
  function handleToggleDbIndex(fieldName, currentlyOn) {
    if (!selectedDb) return;
    setDbIndexConfirm({ fieldName, currentlyOn });
  }

  // Off→on queues an async build (status flows Pending→Building→Active); on→off drops it.
  async function performDbIndexToggle(fieldName, currentlyOn) {
    if (!selectedDb) return;
    setError("");
    setSuccess("");
    if (currentlyOn) {
      setIndexes(prev => prev.map(ix =>
        ix.fieldName === fieldName ? { ...ix, dbIndexStatus: null, dbIndexName: null, dbIndexError: null } : ix
      ));
      try {
        const res = await dropColumnIndex(token, selectedDb, fieldName);
        if (res?.script) {
          downloadSqlText(res.script, `fastsearch_drop_${fieldName}`);
          setSuccess(t("admin.dbIndexDropScriptNote", { defaultValue: "Fast Search turned off. A drop script was downloaded — have your DBA run it as the owner schema." }));
        } else {
          setSuccess(t("admin.dbIndexDropped", { defaultValue: "Fast search turned off." }));
        }
      } catch (e) {
        setError(friendlyMessageForApiError(e));
      } finally {
        const res = await getDatabaseIndexes(token, selectedDb).catch(() => null);
        if (res) setIndexes(res?.indexes || res || []);
      }
    } else {
      setIndexes(prev => prev.map(ix =>
        ix.fieldName === fieldName ? { ...ix, dbIndexStatus: "Pending", dbIndexError: null } : ix
      ));
      try {
        const res = await createColumnIndex(token, selectedDb, fieldName);
        if (res?.script) {
          // Locked-down (DML-only): the app can't CREATE INDEX — a DBA script was returned.
          downloadSqlText(res.script, `fastsearch_${fieldName}`);
          setSuccess(t("admin.dbIndexScriptNote", { defaultValue: "Fast Search needs a database index. A script was downloaded — have your DBA run it as the owner schema; it activates automatically once the index exists." }));
        } else {
          setSuccess(t("admin.dbIndexQueued", { defaultValue: "Fast search is being set up." }));
        }
      } catch (e) {
        setError(friendlyMessageForApiError(e));
      } finally {
        const res = await getDatabaseIndexes(token, selectedDb).catch(() => null);
        if (res) setIndexes(res?.indexes || res || []);
      }
    }
  }

  // While any DB index is queued/building, poll so the UI reflects async progress.
  const anyDbIndexInFlight = useMemo(
    () => (indexes || []).some((ix) => {
      const s = String(ix.dbIndexStatus || "").toLowerCase();
      return s === "pending" || s === "building";
    }),
    [indexes]
  );

  useEffect(() => {
    if (!selectedDb || !anyDbIndexInFlight) return;
    const id = setInterval(async () => {
      const res = await getDatabaseIndexes(token, selectedDb).catch(() => null);
      if (res) setIndexes(res?.indexes || res || []);
    }, 4000);
    return () => clearInterval(id);
  }, [anyDbIndexInFlight, selectedDb, token]);

  async function handleConfirmArchive() {
    if (!deleteModalTarget) return;
    const { id: dbId, label: dbLabel, isClosed: currentlyClosed } = deleteModalTarget;
    setDeleteModalTarget(null);
    try {
      setLoading(true);
      await setDatabaseClosed(token, dbId, !currentlyClosed);
      const dbs = await getDatabases(token);
      setDatabases(dbs || []);
      setSuccess(
        currentlyClosed
          ? t("admin.databaseUnarchived", { dbName: dbLabel, defaultValue: `Database "${dbLabel}" unarchived.` })
          : t("admin.databaseArchived", { dbName: dbLabel, defaultValue: `Database "${dbLabel}" archived.` })
      );
    } catch (e) {
      setError(friendlyMessageForApiError(e));
      setRawErrorDetails(e && e.details ? e.details : e);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmPurge() {
    if (!deleteModalTarget) return;
    const { id: dbId, label: dbLabel } = deleteModalTarget;
    setDeleteModalTarget(null);
    try {
      setLoading(true);
      await purgeDatabase(token, dbId);
      setSuccess(
        t("admin.databasePurged", { dbName: dbLabel, defaultValue: `All data in "${dbLabel}" has been purged. Schema preserved.` })
      );
    } catch (e) {
      setError(friendlyMessageForApiError(e));
      setRawErrorDetails(e && e.details ? e.details : e);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteModalTarget) return;
    const { id: dbId, label: dbLabel } = deleteModalTarget;
    setDeleteModalTarget(null);
    try {
      setLoading(true);
      await deleteDatabase(token, dbId);
      const dbs = await getDatabases(token);
      setDatabases(dbs || []);
      if (String(selectedDb) === String(dbId)) {
        const next = (dbs || []).find((d) => String(getDbId(d)) !== String(dbId));
        setSelectedDb(next ? getDbId(next) : "");
        setIndexes([]);
      }
      setSuccess(
        t("admin.databaseDeleted", { dbName: dbLabel, defaultValue: `Database "${dbLabel}" permanently deleted.` })
      );
    } catch (e) {
      setError(friendlyMessageForApiError(e));
      setRawErrorDetails(e && e.details ? e.details : e);
    } finally {
      setLoading(false);
    }
  }

  // ── Bulk selection helpers ──────────────────────────────────────────────────
  function toggleSelectMode() {
    setSelectMode((prev) => {
      if (prev) setSelectedDbIds([]);
      return !prev;
    });
  }

  function toggleSelectId(id) {
    const idStr = String(id);
    setSelectedDbIds((prev) =>
      prev.includes(idStr) ? prev.filter((x) => x !== idStr) : [...prev, idStr]
    );
  }

  function selectAllVisible(visibleIds) {
    setSelectedDbIds((prev) => {
      const set = new Set(prev.map(String));
      (visibleIds || []).forEach((id) => set.add(String(id)));
      return Array.from(set);
    });
  }

  function clearSelection(visibleIds) {
    if (!visibleIds) {
      setSelectedDbIds([]);
      return;
    }
    const drop = new Set(visibleIds.map(String));
    setSelectedDbIds((prev) => prev.filter((x) => !drop.has(String(x))));
  }

  function getSelectedDbObjects() {
    const set = new Set(selectedDbIds.map(String));
    return (databases || [])
      .filter((d) => set.has(String(getDbId(d))))
      .map((d) => ({
        id: getDbId(d),
        label: getDbLabel(d) || String(getDbId(d)),
        isClosed: !!(d.isClosed || d.IsClosed),
      }));
  }

  async function runBulk(action, label) {
    const targets = getSelectedDbObjects();
    if (targets.length === 0) return;
    setBulkBusy(true);
    setError("");
    setSuccess("");
    try {
      const results = await Promise.allSettled(
        targets.map((tg) => action(tg))
      );
      const succeeded = [];
      const failed = [];
      results.forEach((r, i) => {
        if (r.status === "fulfilled") succeeded.push(targets[i]);
        else failed.push({ target: targets[i], reason: r.reason });
      });

      // Refresh databases list and clear selection
      const dbs = await getDatabases(token).catch(() => null);
      if (dbs) setDatabases(dbs || []);

      // If the currently selected db was deleted, pick another
      const removedSet = new Set(succeeded.map((s) => String(s.id)));
      if (removedSet.has(String(selectedDb))) {
        const next = (dbs || []).find((d) => !removedSet.has(String(getDbId(d))));
        setSelectedDb(next ? getDbId(next) : "");
        setIndexes([]);
      }

      // Clear successful items from selection; keep failed for retry
      setSelectedDbIds((prev) =>
        prev.filter((id) => !removedSet.has(String(id)))
      );

      if (failed.length === 0) {
        setSuccess(
          t("admin.bulkActionSuccess", {
            count: succeeded.length,
            action: label,
            defaultValue: `${label}: ${succeeded.length} database(s) processed successfully.`,
          })
        );
        setBulkModalOpen(false);
        setSelectMode(false);
      } else {
        const failedSummary = failed
          .slice(0, 3)
          .map((f) => `${f.target.label} (${friendlyMessageForApiError(f.reason)})`)
          .join("; ");
        const more = failed.length > 3 ? `; +${failed.length - 3} more` : "";
        setError(
          t("admin.bulkActionPartial", {
            ok: succeeded.length,
            fail: failed.length,
            action: label,
            details: failedSummary + more,
            defaultValue: `${label}: ${succeeded.length} succeeded, ${failed.length} failed — ${failedSummary}${more}`,
          })
        );
        setBulkModalOpen(false);
      }
    } catch (e) {
      setError(friendlyMessageForApiError(e));
    } finally {
      setBulkBusy(false);
    }
  }

  function handleBulkArchive() {
    runBulk(
      (tg) => setDatabaseClosed(token, tg.id, true),
      t("admin.archiveOption", { defaultValue: "Archive" })
    );
  }

  function handleBulkRestore() {
    runBulk(
      (tg) => setDatabaseClosed(token, tg.id, false),
      t("admin.restoreOption", { defaultValue: "Restore" })
    );
  }

  function handleBulkPurge() {
    runBulk(
      (tg) => purgeDatabase(token, tg.id),
      t("admin.purgeOption", { defaultValue: "Purge data" })
    );
  }

  function handleBulkDelete() {
    runBulk(
      (tg) => deleteDatabase(token, tg.id),
      t("admin.deleteOption", { defaultValue: "Delete permanently" })
    );
  }

  async function handleDatabaseCreated(newDbId) {
    setShowCreateModal(false);
    try {
      const dbs = await getDatabases(token);
      setDatabases(dbs || []);
      if (newDbId != null) setSelectedDb(String(newDbId));
      setSuccess(t("admin.databaseCreated", { defaultValue: "Database created successfully." }));
    } catch (e) {
      setError(friendlyMessageForApiError(e));
    }
  }

  async function handleExportConfig() {
    if (!selectedDb) return;
    try {
      const snapshot = await exportDatabaseConfig(token, selectedDb);
      const json = JSON.stringify(snapshot, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${snapshot.sourceDatabaseName || "db-config"}.depconfig.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(friendlyMessageForApiError(e));
    }
  }

  async function handleImportConfig(e) {
    const file = e.target.files?.[0];
    if (!file || !selectedDb) return;
    e.target.value = "";
    try {
      const text = await file.text();
      const config = JSON.parse(text);
      // Ask about Fast Search rebuild before running the import.
      setPendingImport({ kind: "single", config });
    } catch (e) {
      setError(
        e instanceof SyntaxError
          ? t("admin.importConfigInvalidJson", { defaultValue: "Invalid JSON file." })
          : friendlyMessageForApiError(e)
      );
    }
  }

  async function doImportConfig(config, rebuildFastSearch) {
    if (!selectedDb) return;
    try {
      setLoading(true);
      const result = await importDatabaseConfig(token, selectedDb, config, rebuildFastSearch);
      // Reload indexes and the full databases list so propertiesJson is fresh
      const [idxRes, dbsRes] = await Promise.allSettled([
        getDatabaseIndexes(token, selectedDb),
        getDatabases(token),
      ]);
      if (idxRes.status === "fulfilled") setIndexes(idxRes.value?.indexes || idxRes.value || []);
      if (dbsRes.status === "fulfilled") setDatabases(dbsRes.value || []);
      const nScripts = downloadImportScripts(result?.scripts, selectedDbLabel);
      const scriptNote = nScripts > 0
        ? " " + t("admin.importProvisioningScriptsNote", {
            count: nScripts,
            defaultValue: `${nScripts} provisioning script(s) downloaded — have your DBA run them as the owner schema, then Activate.`,
          })
        : "";
      const fsqNote = result?.fastSearchQueued > 0
        ? " " + t("admin.fastSearchQueuedNote", {
            count: result.fastSearchQueued,
            defaultValue: `${result.fastSearchQueued} Fast Search index build(s) queued.`,
          })
        : "";
      setSuccess(
        t("admin.importConfigSuccess", {
          added: result.indexesAdded,
          skipped: result.indexesSkipped,
          defaultValue: `Import complete: ${result.indexesAdded} index(es) added, ${result.indexesSkipped} skipped.`,
        }) + fsqNote + scriptNote
      );
    } catch (e) {
      setError(
        e instanceof SyntaxError
          ? t("admin.importConfigInvalidJson", { defaultValue: "Invalid JSON file." })
          : friendlyMessageForApiError(e)
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleBundleExportConfirm(selectedIds) {
    setBundleBusy(true);
    setBundleError("");
    try {
      const bundle = await exportDatabasesBundle(token, selectedIds);
      const json = JSON.stringify(bundle, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `databases-${ts}.depbundle.json`;
      a.click();
      URL.revokeObjectURL(url);
      setShowBundlePicker(false);
      setSuccess(
        t("admin.bundleExportSuccess", {
          count: (bundle?.databases?.length) || selectedIds.length,
          defaultValue: `Bundle exported with ${(bundle?.databases?.length) || selectedIds.length} database(s).`,
        })
      );
    } catch (e) {
      setBundleError(friendlyMessageForApiError(e));
    } finally {
      setBundleBusy(false);
    }
  }

  async function handleImportBundle(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      if (!bundle || !Array.isArray(bundle.databases)) {
        setError(t("admin.bundleInvalidFile", { defaultValue: "Invalid bundle file." }));
        return;
      }
      // Ask about Fast Search rebuild before running the bundle import.
      setPendingImport({ kind: "bundle", bundle });
    } catch (e) {
      setError(
        e instanceof SyntaxError
          ? t("admin.importConfigInvalidJson", { defaultValue: "Invalid JSON file." })
          : friendlyMessageForApiError(e)
      );
    }
  }

  async function doImportBundle(bundle, rebuildFastSearch) {
    try {
      setLoading(true);
      const result = await importDatabasesBundle(token, bundle, true, rebuildFastSearch);
      const dbsRes = await getDatabases(token).catch(() => null);
      if (dbsRes) setDatabases(dbsRes || []);
      const warnings = (result?.warnings || []).join(" — ");
      const fsqNote = result?.fastSearchIndexesQueued > 0
        ? " " + t("admin.fastSearchQueuedNote", {
            count: result.fastSearchIndexesQueued,
            defaultValue: `${result.fastSearchIndexesQueued} Fast Search index build(s) queued.`,
          })
        : "";
      const nScripts = downloadImportScripts(result?.scripts, "bundle");
      const scriptNote = nScripts > 0
        ? " " + t("admin.importProvisioningScriptsNote", {
            count: nScripts,
            defaultValue: `${nScripts} provisioning script(s) downloaded — have your DBA run them as the owner schema, then Activate.`,
          })
        : "";
      setSuccess(
        t("admin.bundleImportSuccess", {
          processed: result.databasesProcessed || 0,
          created: result.databasesCreated || 0,
          skipped: result.databasesSkipped || 0,
          indexesAdded: result.indexesAdded || 0,
          dropFoldersAdded: result.dropFoldersAdded || 0,
          defaultValue: `Bundle import complete: ${result.databasesProcessed || 0} processed (${result.databasesCreated || 0} created, ${result.databasesSkipped || 0} skipped), ${result.indexesAdded || 0} index(es) added, ${result.dropFoldersAdded || 0} drop folder(s) added.${warnings ? " Warnings: " + warnings : ""}`,
        }) + fsqNote + scriptNote + (warnings ? " " + warnings : "")
      );
    } catch (e) {
      setError(
        e instanceof SyntaxError
          ? t("admin.importConfigInvalidJson", { defaultValue: "Invalid JSON file." })
          : friendlyMessageForApiError(e)
      );
    } finally {
      setLoading(false);
    }
  }

  // Resolve the Fast Search modal choice: run the pending import with the answer.
  function handleFastSearchChoice(rebuild) {
    const p = pendingImport;
    setPendingImport(null);
    if (!p) return;
    if (p.kind === "single") doImportConfig(p.config, rebuild);
    else if (p.kind === "bundle") doImportBundle(p.bundle, rebuild);
  }

  // Locked-down (DML-only): once the DBA has run the provisioning script, verify
  // the table and flip the base from Pending to active.
  async function handleActivateDatabase() {
    if (!selectedDb) return;
    try {
      setLoading(true);
      const res = await activateDatabase(token, selectedDb);
      if (res?.success && res?.active) {
        const dbs = await getDatabases(token);
        setDatabases(dbs || []);
        setSuccess(t("admin.databaseActivated", {
          defaultValue: "Document base activated.",
        }));
      } else {
        setError(res?.message || t("admin.databaseActivateNotReady", {
          defaultValue: "The table has not been provisioned yet. Run the script, then activate.",
        }));
      }
    } catch (e) {
      setError(friendlyMessageForApiError(e));
    } finally {
      setLoading(false);
    }
  }

  // Locked-down (DML-only): re-fetch and download the owner-run provisioning script
  // for a Pending base. The create dialog shows it only once, so this recovers it for
  // the DBA (often a different person, days later).
  async function handleDownloadProvisioningScript() {
    if (!selectedDb) return;
    try {
      const res = await getProvisioningScript(token, selectedDb);
      if (!res?.success || !res?.script) {
        setError(res?.message || t("admin.provisioningScriptError", {
          defaultValue: "Could not generate the provisioning script.",
        }));
        return;
      }
      const safeName = (selectedDbLabel || "repository").replace(/[^\w.-]+/g, "_");
      const blob = new Blob([res.script], { type: "application/sql" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `provision_${safeName}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(friendlyMessageForApiError(e));
    }
  }

  // Locked-down (DML-only): download a single owner-run SQL script (e.g. a Fast Search
  // CREATE/DROP INDEX the DBA must run).
  function downloadSqlText(text, filenameBase) {
    if (!text) return;
    const blob = new Blob([text], { type: "application/sql" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `${(filenameBase || "script").replace(/[^\w.-]+/g, "_")}_${ts}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Locked-down (DML-only): import can emit owner-run provisioning scripts (CREATE for a
  // Pending table, ALTER for deferred searchable fields). Bundle them into one .sql for the
  // DBA. Returns how many scripts were written.
  function downloadImportScripts(scripts, filenameBase) {
    if (!Array.isArray(scripts) || scripts.length === 0) return 0;
    const header =
      "-- ============================================================================\n" +
      "-- Depository — provisioning scripts from import (locked-down / DML-only)\n" +
      "-- Run connected AS the owner schema. For each base, run its 'table' (CREATE)\n" +
      "-- section before the matching 'indexes' (ALTER) section, then Activate in the UI.\n" +
      "-- ============================================================================\n\n";
    const body = scripts
      .map((s) => `-- ---- ${s.database} (${s.kind}) ----\n${s.script}`)
      .join("\n\n");
    const blob = new Blob([header + body + "\n"], { type: "application/sql" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `provision_${(filenameBase || "import").replace(/[^\w.-]+/g, "_")}_${ts}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return scripts.length;
  }

  const selectedDbObject =
    databases && databases.length > 0
      ? databases.find((d) => getDbId(d) === selectedDb)
      : null;
  const selectedDbLabel = selectedDbObject ? getDbLabel(selectedDbObject) : "";
  const selectedDbWithStats = selectedDbObject && dbStats
    ? { ...selectedDbObject, ...dbStats }
    : selectedDbObject;

  const orderedIndexes = useMemo(() => {
    if (!selectedDb) return indexes;
    try {
      const key = `indexesOrder:${selectedDb}`;
      const stored = window.localStorage.getItem(key);
      if (!stored) return indexes;
      const order = JSON.parse(stored);
      if (!Array.isArray(order) || order.length === 0) return indexes;
      const byField = new Map(
        (indexes || []).map((ix) => [String(ix.fieldName), ix])
      );
      const result = [];
      order.forEach((field) => {
        const keyField = String(field);
        if (byField.has(keyField)) {
          result.push(byField.get(keyField));
          byField.delete(keyField);
        }
      });
      byField.forEach((value) => result.push(value));
      return result;
    } catch {
      return indexes;
    }
  }, [indexes, selectedDb]);

  useEffect(() => {
    if (!selectedDb || !indexes || indexes.length === 0) return;
    try {
      const key = `indexesOrder:${selectedDb}`;
      const fields = indexes.map((ix) => String(ix.fieldName));
      window.localStorage.setItem(key, JSON.stringify(fields));
    } catch {
      // ignore persistence errors
    }
  }, [indexes, selectedDb]);

  const handleReorder = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setIndexes((prev) => {
      if (!prev || prev.length === 0) return prev;
      const next = [...prev];
      if (
        fromIndex < 0 ||
        fromIndex >= next.length ||
        toIndex < 0 ||
        toIndex >= next.length
      ) {
        return prev;
      }
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />

      <div className="page-container">
        <div className="indexes-page">
          <Hero
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
                <ellipse cx="12" cy="5" rx="8" ry="3" />
                <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
                <path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
              </svg>
            }
            title={t("admin.indexes")}
            subtitle={t("admin.indexesSubtitle", {
              defaultValue: "Plan which fields are searchable for each database.",
            })}
            art={<StackArt />}
          />

          <div className="indexes-shell">
            <div className="indexes-card">
              <div className="indexesShell">
                <DatabaseStackRail
                  databases={databases}
                  selectedDatabaseId={selectedDb}
                  onSelectDatabase={setSelectedDb}
                  getDbId={getDbId}
                  getDbLabel={getDbLabel}
                  searchQuery={dbSearch}
                  onSearchQueryChange={setDbSearch}
                  indexesCount={orderedIndexes.length}
                  loading={loading}
                  canDelete={isAdmin}
                  onRequestDelete={setDeleteModalTarget}
                  onCreateDatabase={isAdmin ? () => setShowCreateModal(true) : undefined}
                  onExportBundle={isAdmin ? () => { setBundleError(""); setShowBundlePicker(true); } : undefined}
                  onImportBundle={isAdmin ? handleImportBundle : undefined}
                  importBundleFileRef={importBundleFileRef}
                  selectMode={isAdmin && selectMode}
                  onToggleSelectMode={isAdmin ? toggleSelectMode : undefined}
                  selectedIds={selectedDbIds}
                  onToggleSelectId={toggleSelectId}
                  onSelectAllVisible={selectAllVisible}
                  onClearSelection={clearSelection}
                  onRequestBulkDelete={() => setBulkModalOpen(true)}
                />

                <DatabaseForegroundPanel hasSelection={!!selectedDb}>
                  <div className="indexes-main">
                    {error && (
                      <Alert type="error" onClose={() => setError("")}>
                        {error}
                      </Alert>
                    )}
                    {error && rawErrorDetails && (
                      <ErrorDetailsAccordion details={rawErrorDetails} />
                    )}
                    {success && (
                      <Alert type="success" onClose={() => setSuccess("")}>
                        {success}
                      </Alert>
                    )}

                    <div className="dbMainHeader">
                      <DatabaseHeader
                        selectedDatabaseLabel={selectedDbLabel}
                        indexesCount={orderedIndexes.length}
                        hasSelection={!!selectedDb}
                        isProvisioningPending={!!selectedDbObject?.isProvisioningPending}
                        onActivateClick={isAdmin && selectedDbObject?.isProvisioningPending ? handleActivateDatabase : undefined}
                        onDownloadScript={isAdmin && selectedDbObject?.isProvisioningPending ? handleDownloadProvisioningScript : undefined}
                        onAddIndexClick={selectedDb && activeTab === "indexes" ? () => setShowInlineAdd(true) : undefined}
                        onExportConfig={isAdmin && selectedDb ? handleExportConfig : undefined}
                        onImportConfig={isAdmin && selectedDb ? handleImportConfig : undefined}
                        importFileRef={importFileRef}
                      />
                    </div>

                    {selectedDb ? (
                      <>
                        <DatabaseKpiStrip
                          database={selectedDbWithStats}
                          indexesCount={orderedIndexes.length}
                        />

                        <div className="dbTabBar">
                          <button className={`dbTab${activeTab === "indexes" ? " active" : ""}`} onClick={() => setActiveTab("indexes")}>Indexes</button>
                          {isAdmin && <button className={`dbTab${activeTab === "properties" ? " active" : ""}`} onClick={() => setActiveTab("properties")}>Database Properties</button>}
                          {isAdmin && <button className={`dbTab${activeTab === "mapping" ? " active" : ""}`} onClick={() => setActiveTab("mapping")}>Index Mapping</button>}
                          <button className={`dbTab${activeTab === "browse" ? " active" : ""}`} onClick={() => setActiveTab("browse")}>{t("admin.browse.tab")}</button>
                        </div>

                        {activeTab === "indexes" && (
                          <>
                            <IndexesTable
                              indexes={orderedIndexes}
                              canDelete={features.enableDbDelete || isAdmin}
                              onDelete={handleDelete}
                              onReorder={handleReorder}
                              onUpdateDataType={isAdmin ? handleUpdateDataType : undefined}
                              onUpdateLength={isAdmin ? handleUpdateLength : undefined}
                              onToggleDbIndex={isAdmin ? handleToggleDbIndex : undefined}
                              showAddRow={showInlineAdd}
                              addFormRef={addFormRef}
                              currentUserDisplayName={currentUserDisplayName}
                              newFieldName={newFieldName}
                              newDisplayName={newDisplayName}
                              newDataType={newDataType}
                              newIsDeletable={newIsDeletable}
                              setNewFieldName={setNewFieldName}
                              setNewDisplayName={setNewDisplayName}
                              setNewDataType={setNewDataType}
                              setNewIsDeletable={setNewIsDeletable}
                              isSavingIndex={isSavingIndex}
                              onSubmitNew={handleAdd}
                              onCancelNew={() => {
                                setShowInlineAdd(false);
                                setNewFieldName("");
                                setNewDisplayName("");
                                setNewDataType("string");
                                setNewIsDeletable(true);
                              }}
                            />
                          </>
                        )}

                        {activeTab === "browse" && (
                          <BrowseRecordsPanel
                            database={selectedDbObject}
                            token={token}
                          />
                        )}

                        {isAdmin && activeTab === "properties" && (
                          <div className="dbPropsStorageBar">
                            <button
                              type="button"
                              className="dbProps-dropfolder-link"
                              onClick={() => setShowStorageLocations(true)}
                            >
                              🗄 {t("storage.locations.manage")} →
                            </button>
                          </div>
                        )}

                        {isAdmin && (activeTab === "properties" || activeTab === "mapping") && (
                          <DatabasePropertiesPanel
                            database={selectedDbObject}
                            token={token}
                            section={activeTab}
                            onPropertiesSaved={(newPropertiesJson) => {
                              setDatabases(prev =>
                                prev.map(d =>
                                  String(getDbId(d)) === String(selectedDb)
                                    ? { ...d, propertiesJson: newPropertiesJson }
                                    : d
                                )
                              );
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <div className="indexes-empty-state">
                        {t("admin.selectPlaceholder", {
                          defaultValue:
                            "Choose a database card above to manage its indexes.",
                        })}
                      </div>
                    )}
                  </div>
                </DatabaseForegroundPanel>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteDatabaseModal
        target={deleteModalTarget}
        onArchive={handleConfirmArchive}
        onPurge={handleConfirmPurge}
        onDelete={handleConfirmDelete}
        onCancel={() => setDeleteModalTarget(null)}
      />

      <FastSearchImportModal
        open={!!pendingImport}
        kind={pendingImport?.kind}
        onChoose={handleFastSearchChoice}
        onCancel={() => setPendingImport(null)}
      />

      <DbIndexConfirmModal
        target={dbIndexConfirm}
        onCancel={() => setDbIndexConfirm(null)}
        onConfirm={() => {
          const c = dbIndexConfirm;
          setDbIndexConfirm(null);
          if (c) performDbIndexToggle(c.fieldName, c.currentlyOn);
        }}
      />

      <ConfirmModal
        open={!!deleteFieldTarget}
        variant="danger"
        icon="🗑"
        title={t("admin.deleteButton", { defaultValue: "Delete" })}
        name={deleteFieldTarget?.fieldName}
        body={t("admin.deleteConfirm", {
          fieldName: deleteFieldTarget?.fieldName || "",
          defaultValue:
            "Delete field {{fieldName}}? This will remove metadata and the column (if permitted).",
        })}
        confirmLabel={t("admin.deleteButton", { defaultValue: "Delete" })}
        onConfirm={() => {
          const tgt = deleteFieldTarget;
          setDeleteFieldTarget(null);
          if (tgt) performDeleteField(tgt.fieldName);
        }}
        onCancel={() => setDeleteFieldTarget(null)}
      />

      <ConfirmModal
        open={!!lenConfirmTarget}
        variant="warn"
        icon="📏"
        title={t("admin.widenConfirmTitle", { defaultValue: "Allow longer entries?" })}
        name={lenConfirmTarget?.fieldName}
        body={t("admin.widenConfirmBody", {
          n: lenConfirmTarget?.newLength || 0,
          defaultValue:
            "This lets “{{field}}” hold longer entries — up to {{n}} characters. Anything already saved is kept as-is; only the maximum goes up. This can’t be lowered again later.",
          field: lenConfirmTarget?.fieldName || "",
        })}
        confirmLabel={t("admin.widenConfirmButton", { defaultValue: "Yes, allow longer" })}
        onConfirm={() => {
          const tgt = lenConfirmTarget;
          setLenConfirmTarget(null);
          if (tgt) performUpdateLength(tgt.fieldName, tgt.newLength);
        }}
        onCancel={() => setLenConfirmTarget(null)}
      />

      {bulkModalOpen && (
        <BulkDeleteDatabasesModal
          targets={getSelectedDbObjects()}
          busy={bulkBusy}
          onArchive={handleBulkArchive}
          onRestore={handleBulkRestore}
          onPurge={handleBulkPurge}
          onDelete={handleBulkDelete}
          onCancel={() => { if (!bulkBusy) setBulkModalOpen(false); }}
        />
      )}

      {showCreateModal && (
        <CreateDatabaseModal
          token={token}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleDatabaseCreated}
        />
      )}

      {showStorageLocations && (
        <StorageLocationsModal
          token={token}
          onClose={() => setShowStorageLocations(false)}
          selectedDatabaseId={selectedDbObject ? (selectedDbObject.id ?? selectedDbObject.Id) : null}
          selectedDatabaseName={selectedDbLabel}
          currentStorageLocationId={selectedDbObject ? (selectedDbObject.storageLocationId ?? selectedDbObject.StorageLocationId ?? null) : null}
          onBindingChanged={(locationId) => {
            setDatabases((prev) =>
              prev.map((d) =>
                String(getDbId(d)) === String(selectedDb) ? { ...d, storageLocationId: locationId } : d
              )
            );
          }}
        />
      )}

      {showBundlePicker && (
        <BundlePickerModal
          databases={databases}
          getDbId={getDbId}
          getDbLabel={getDbLabel}
          onCancel={() => setShowBundlePicker(false)}
          onConfirm={handleBundleExportConfirm}
          busy={bundleBusy}
          error={bundleError}
        />
      )}
    </div>
  );
}
